import {
  type SiteDetailResponse,
  type SiteResponse,
  v2SitesDetailsDetail,
} from '@on3/api/src';
import { Hide } from '@on3/ui-lib/components/Hide';
import { externalApi, useSite } from '@on3/ui-lib/index';
import { type AltPromoType, type PromoType } from '@on3/ui-lib/types/purchase';
import { JoinAppPromo } from '@public/components/Join/AppPromo';
import { JoinFaq } from '@public/components/Join/Faq';
import { JoinForum } from '@public/components/Join/Forum';
import { JoinHero } from '@public/components/Join/Hero';
import { JoinOneSubscription } from '@public/components/Join/OneSubscription';
import { JoinRivals } from '@public/components/Join/Rivals';
import { JoinSportsNetwork } from '@public/components/Join/SportsNetwork';
import { JoinTeamSelect } from '@public/components/Join/TeamSelect';
import { JoinTheAthletic } from '@public/components/Join/TheAthletic';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import styles from './JoinPage.module.scss';

enum TEAM_SELECT_PATHS {
  JOIN = '/join/',
}

export const JoinPage = ({
  siteDetails,
  sites = [],
  promo,
}: {
  siteDetails: SiteDetailResponse | null;
  sites?: SiteResponse[];
  promo?: PromoType | AltPromoType;
}) => {
  const { currentSite } = useSite();
  const pathname = usePathname();

  const [selectedSite, setSelectedSite] = useState<SiteResponse | null>(null);
  const [selectedDetails, setSelectedDetails] =
    useState<SiteDetailResponse | null>(null);

  const siteVars = useMemo(
    () =>
      Object.entries({
        '--site-primary-color': selectedSite?.primaryColor,
        '--site-link-color':
          selectedSite?.linkColor || selectedSite?.primaryColor,
      })
        .filter(([, v]) => !!v)
        .map(([k, v]) => `${k}: ${v};`)
        .join(' '),
    [selectedSite],
  );

  useEffect(() => {
    if (!selectedSite) return;

    const fetchSiteDetails = async (siteKey: number) => {
      try {
        const res = await v2SitesDetailsDetail(externalApi, siteKey);

        setSelectedDetails(res);
      } catch (error) {
        setSelectedDetails(null);
      }
    };

    fetchSiteDetails(selectedSite.key);
  }, [selectedSite]);

  const site = selectedSite ? selectedSite : currentSite;
  const details = selectedSite ? selectedDetails : siteDetails;

  const showTeamSelect = [TEAM_SELECT_PATHS.JOIN].includes(
    pathname as string as TEAM_SELECT_PATHS,
  );
  const showSportsNetwork = !promo;
  const showRivals = !promo;
  const showTheAthletic = !promo;
  const showForum = site.isTeam && details && site.primaryForumId;

  return (
    <>
      {selectedSite && <style>{`:root { ${siteVars} } `}</style>}
      <div className={styles.layout}>
        <JoinHero
          promo={promo}
          site={selectedSite}
          teamSelect={showTeamSelect}
        />
        <div className={styles.container}>
          <div className={styles.modules}>
            {showTeamSelect && (
              <JoinTeamSelect
                select={setSelectedSite}
                site={selectedSite}
                sites={sites}
              />
            )}
            <JoinOneSubscription details={details} site={site} />
            {showForum && <JoinForum details={details} site={site} />}
            {showSportsNetwork && <JoinSportsNetwork sites={sites} />}
            {showRivals && <JoinRivals />}
            {showTheAthletic && <JoinTheAthletic />}
            <JoinAppPromo site={site} />
            <JoinFaq />
          </div>
        </div>
      </div>
      <Hide classes={['.adthrive-ad', '.adhesionAd', '[data-gg-moat]']} />
    </>
  );
};
